<template>
  <!-- Outbound Journey Section -->
  <Card v-if="repInfo" :title="__DICT__.outboundJourney" margin="mb-4">
      <template v-if="!showOutbound">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <div class="alert alert-info mb-0">
          {{ __DICT__.repTravel.alreadyInResort }}{{ previousResort ? ` - ${__DICT__.repTravel.previousResort}: ${previousResort}` : '' }}
        </div>
      </template>
      <template v-else>
        <div class="row g-3">
          <!-- Flight Information -->
          <div class="col-md-6" v-if="hasOutboundFlight">
            <div class="card">
              <div class="card-stamp">
                <div class="card-stamp-icon bg-primary">
                  <icon-plane-inflight size="64"></icon-plane-inflight>
                </div>
              </div>
              <div class="card-body">
                <div class="row align-items-center g-3">
                  <template v-for="(item, index) in outboundFlightInfo" :key="index">
                    <div :class="item.class" v-if="item.value">
                      <div class="text-secondary">{{ item.label }}</div>
                      <div class="h3 m-0">{{ item.value }}</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Transport Information -->
          <div class="col-md-6" v-if="hasOutboundTransport">
            <div class="card">
              <div class="card-stamp">
                <div class="card-stamp-icon bg-secondary">
                  <IconBus size="64"></IconBus>
                </div>
              </div>
              <div class="card-body">
                <div class="row align-items-center g-3">
                  <template v-for="(item, index) in outboundTransportInfo" :key="index">
                    <div v-if="item.label === __DICT__.repTravel.transportRef && item.value[0] === 'T'">
                      <div class="text-secondary">{{ item.label }}</div>
                      <router-link :to="travelLink(item,'transfer')"><div class="h3 m-0">{{ item.value }}</div></router-link>
                    </div>
                    <div v-else-if="item.label === __DICT__.repTravel.transportRef && item.value[1] === 'C'">
                      <div class="text-secondary">{{ item.label }}</div>
                      <router-link :to="travelLink(item,'coach')"><div class="h3 m-0">{{ item.value }}</div></router-link>
                    </div>
                    <div v-else>
                      <div class="text-secondary">{{ item.label }}</div>
                      <div class="h3 m-0">{{ item.value }}</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
  </Card>

  <!-- Inbound Journey Section -->
  <Card v-if="repInfo" :title="__DICT__.inboundJourney" margin="">
      <template v-if="!showInbound">
        <div class="alert alert-info mb-0">
          {{ inboundAlertMessage }}
        </div>
      </template>
      <template v-else>
        <div class="row g-3">
          <!-- Flight Information -->
          <div class="col-md-6" v-if="hasInboundFlight">
            <div class="card">
              <div class="card-stamp">
                <div class="card-stamp-icon bg-primary">
                  <IconPlaneInflight size="64"></IconPlaneInflight>
                </div>
              </div>
              <div class="card-body">
                <div class="row align-items-center g-3">
                  <template v-for="(item, index) in inboundFlightInfo" :key="index">
                    <div :class="item.class" v-if="item.value">
                      <div class="text-secondary">{{ item.label }}</div>
                      <div class="h3 m-0">{{ item.value }}</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Transport Information -->
          <div class="col-md-6" v-if="hasInboundTransport">
            <div class="card">
              <div class="card-stamp">
                <div class="card-stamp-icon bg-secondary">
                  <IconBus size="64"></IconBus>
                </div>
              </div>
              <div class="card-body">
                <div class="row align-items-center g-3">
                  <template v-for="(item, index) in inboundTransportInfo" :key="index">
                    <div :class="item.class" v-if="item.value">
                      <div v-if="item.label === __DICT__.repTravel.transportRef && item.value[0] === 'T'">
                        <div class="text-secondary">{{ item.label }}</div>
                        <router-link :to="travelLink(item,'transfer')"><div class="h3 m-0">{{ item.value }}</div></router-link>
                      </div>
                      <div v-else-if="item.label === __DICT__.repTravel.transportRef && item.value[1] === 'C'">
                        <div class="text-secondary">{{ item.label }}</div>
                        <router-link :to="travelLink(item,'coach')"><div class="h3 m-0">{{ item.value }}</div></router-link>
                      </div>
                      <div v-else>
                        <div class="text-secondary">{{ item.label }}</div>
                        <div class="h3 m-0">{{ item.value }}</div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
  </Card>
</template>

<script setup>
import {computed} from "vue";
import {IconPlaneInflight, IconBus} from "@tabler/icons-vue"
import { Card } from "@/components/atomic";

const props = defineProps({
  repInfo: {
    type: Object,
    required: true,
    default: () => ({})
  }
});

const getValue = (obj, key) => {
  if (!obj || !key) return null;
  const value = obj[key];
  if (value === null || value === undefined || value === '#N/A' || value === 'null') return null;
  return value;
};

const previousResort = computed(() => getValue(props.repInfo, 'Previous Resort'));

const showOutbound = computed(() => {
  const transport = getValue(props.repInfo, 'OB Transport');
  return Boolean(transport && transport !== "Already in resort");
});

const showInbound = computed(() => {
  const transport = getValue(props.repInfo, 'IB Transport');
  return Boolean(transport && transport !== "Stays in resort");
});

const hasOutboundFlight = computed(() => {
  if (!props.repInfo) return false;
  return Boolean(
      getValue(props.repInfo, 'OB Flight No') ||
      getValue(props.repInfo, 'OB Route') ||
      getValue(props.repInfo, 'OB TO') ||
      getValue(props.repInfo, 'OB LD')
  );
});

const travelLink = (item,travelType) => {
  return `/${travelType}/${item.value}`
}

const hasInboundFlight = computed(() => {
  if (!props.repInfo) return false;
  return Boolean(
      getValue(props.repInfo, 'IB Flight No') ||
      getValue(props.repInfo, 'IB Date') ||
      getValue(props.repInfo, 'IB Route') ||
      getValue(props.repInfo, 'IB TO') ||
      getValue(props.repInfo, 'IB LD')
  );
});

const hasOutboundTransport = computed(() => {
  if (!props.repInfo) return false;
  return Boolean(
      getValue(props.repInfo, 'OB Transport') ||
      getValue(props.repInfo, 'OB Pickup Point') ||
      getValue(props.repInfo, 'OB Load') ||
      getValue(props.repInfo, 'OB Depart') ||
      getValue(props.repInfo, 'OB Date') ||
      getValue(props.repInfo, 'OB ETA')
  );
});

const hasInboundTransport = computed(() => {
  if (!props.repInfo) return false;
  return Boolean(
      getValue(props.repInfo, 'IB Transport') ||
      getValue(props.repInfo, 'IB Pickup Point') ||
      getValue(props.repInfo, 'IB Load') ||
      getValue(props.repInfo, 'IB Depart') ||
      getValue(props.repInfo, 'IB ETA') ||
      getValue(props.repInfo, 'IB Date')
  );
});

const outboundFlightInfo = computed(() => ([
  {label: __DICT__.repTravel.flightNumber, value: getValue(props.repInfo, 'OB Flight No'), class: 'col-6'},
  {label: __DICT__.date, value: getValue(props.repInfo, 'OB Date'), class: 'col-6'},
  {label: __DICT__.route, value: getValue(props.repInfo, 'OB Route'), class: 'col-6'},
  {label: __DICT__.repTravel.flightRef, value: getValue(props.repInfo, 'OB Flight Ref'), class: 'col-6'},
  {label: __DICT__.repTravel.takeOff, value: getValue(props.repInfo, 'OB TO'), class: 'col-6'},
  {label: __DICT__.repTravel.landing, value: getValue(props.repInfo, 'OB LD'), class: 'col-6'},

]));

const outboundTransportInfo = computed(() => ([
  {label: __DICT__.repTravel.transportRef, value: getValue(props.repInfo, 'OB Transport'), class: 'col-6'},
  {label: __DICT__.repTravel.loadTime, value: getValue(props.repInfo, 'OB Load'), class: 'col-6'},
  {label: __DICT__.repTravel.pickUpPoint, value: getValue(props.repInfo, 'OB Pickup Point'), class: 'col-12'},
  {label: __DICT__.departure, value: getValue(props.repInfo, 'OB Depart'), class: 'col-6'},
  {label: __DICT__.date, value: getValue(props.repInfo, 'OB Date'), class: 'col-6'},
  {label: __DICT__.repTravel.eta, value: getValue(props.repInfo, 'OB ETA'), class: 'col-6'},
]));

const inboundFlightInfo = computed(() => ([
  {label: __DICT__.repTravel.flightNumber, value: getValue(props.repInfo, 'IB Flight No'), class: 'col-6'},
  {label: __DICT__.date, value: getValue(props.repInfo, 'IB Date'), class: 'col-6'},
  {label: __DICT__.route, value: getValue(props.repInfo, 'IB Route'), class: 'col-6'},
  {label: __DICT__.repTravel.flightRef, value: getValue(props.repInfo, 'IB Flight Ref'), class: 'col-6'},
  {label: __DICT__.repTravel.takeOff, value: getValue(props.repInfo, 'IB TO'), class: 'col-6'},
  {label: __DICT__.repTravel.landing, value: getValue(props.repInfo, 'IB LD'), class: 'col-6'},
  {label: __DICT__.date, value: getValue(props.repInfo, 'IB Date'), class: 'col-6'}
]));

const inboundTransportInfo = computed(() => ([
  {label: __DICT__.repTravel.transportRef, value: getValue(props.repInfo, 'IB Transport'), class: 'col-6'},
  {label: __DICT__.repTravel.loadTime, value: getValue(props.repInfo, 'IB Load'), class: 'col-6'},
  {label: __DICT__.repTravel.pickUpPoint, value: getValue(props.repInfo, 'IB Pickup Point'), class: 'col-12'},
  {label: __DICT__.departure, value: getValue(props.repInfo, 'IB Depart'), class: 'col-6'},
  {label: __DICT__.repTravel.eta, value: getValue(props.repInfo, 'IB ETA'), class: 'col-6'},
  {label: __DICT__.repTravel.finalDestination, value: getValue(props.repInfo, 'End Point'), class: 'col-12'}
]));

const inboundAlertMessage = computed(() => {
  if (!props.repInfo) return '';

  const transport = getValue(props.repInfo, 'IB Transport');
  const endPoint = getValue(props.repInfo, 'End Point');
  const nextResort = getValue(props.repInfo, 'Next Resort');

  if (transport === 'Stays in resort') {
    let message = __DICT__.repTravel.staysInResort;
    if (endPoint) {
      message += ` - ${__DICT__.repTravel.finalDestination}: ${endPoint}`;
    }
    if (nextResort) {
      message += ` - ${nextResort}`;
    }
    return message;
  }

  return nextResort ? `${__DICT__.repTravel.noInboundJourney} - ${nextResort}` : __DICT__.repTravel.noInboundJourney;
});
</script>
